import {Ed25519KeyIdentity} from '@dfinity/identity';
import TorusSdk from '@toruslabs/customauth';
import React from 'react';
import useAuthSDK from '../hooks/useAuthSDK';
import useIdentity from '../hooks/useIdentity';
import usePrivateKeyState from '../hooks/usePrivateKeyState';
import {
  GOOGLE_VERIFIER_DETAILS,
  LINKEDIN_VERIFIER_DETAILS,
  PASSWORDLESS_VERIFIER_DETAILS,
  AUTH0_DOMAIN,
} from '../config/auth';

type LoginType = 'google' | 'linkedin' | 'passwordless';

type User = {
  name: string;
  email: string;
  profileImage: string;
};

type AuthContextType = {
  identity: Ed25519KeyIdentity | undefined;
  login: (type: LoginType, login_hint?: string) => Promise<void>;
  user?: User;
  isLoggedIn: boolean;
  authSdk: TorusSdk | undefined;
  privateKey: string;
  isLoading: boolean;
};

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);
AuthContext.displayName = 'AuthContext';

function AuthProvider(props: {children: React.ReactNode}) {
  const authSdk = useAuthSDK();
  const [user, setUser] = React.useState<User | undefined>();
  const [privateKey, setPrivateKey] = usePrivateKeyState();
  const identity = useIdentity(privateKey);
  const [isLoading, setIsLoading] = React.useState(false);

  const isLoggedIn = !!identity;

  const login = React.useCallback(
    async (type: LoginType, login_hint?: string) => {
      try {
        if (!authSdk) {
          throw new Error('authSdk is not defined');
        }
        setIsLoading(true);
        let loginData = GOOGLE_VERIFIER_DETAILS;
        if (type === 'linkedin') {
          loginData = LINKEDIN_VERIFIER_DETAILS;
        }
        if (type === 'passwordless') {
          loginData = PASSWORDLESS_VERIFIER_DETAILS;
          if (login_hint) {
            loginData = {
              ...PASSWORDLESS_VERIFIER_DETAILS,
              jwtParams: {domain: AUTH0_DOMAIN, login_hint},
            };
          }
        }
        const loginResult = await authSdk.triggerLogin(loginData);
        setUser(loginResult.userInfo);
        setPrivateKey(loginResult.privateKey);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    },
    [authSdk, setPrivateKey]
  );

  const logout = React.useCallback(async () => {
    setPrivateKey('');
    setUser(undefined);
  }, [setPrivateKey]);

  const value = React.useMemo(() => {
    return {
      identity,
      login,
      user,
      isLoggedIn,
      authSdk,
      privateKey,
      isLoading,
      logout,
    };
  }, [authSdk, identity, isLoading, isLoggedIn, login, logout, privateKey, user]);

  return <AuthContext.Provider {...props} value={value} />;
}

function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error(`useAuth must be used within a AuthProvider`);
  }
  return context;
}

export {AuthProvider, useAuth};
